import type { SlideProps } from "@/engine/app/offsite-config";

/** Glockenkurve wie auf der LLM-Folie — `spread` steuert, wie spitz sie ist. */
function gaussPath(spread: number, width = 520, height = 150, pad = 10): string {
  const pts: string[] = [];
  const n = 60;
  for (let i = 0; i <= n; i++) {
    const x = i / n;
    const y = Math.exp(-Math.pow((x - 0.5) * spread, 2) / 2);
    const px = pad + x * (width - 2 * pad);
    const py = height - pad - y * (height - 2 * pad);
    pts.push(`${i === 0 ? "M" : "L"}${px.toFixed(1)},${py.toFixed(1)}`);
  }
  return pts.join(" ");
}

export function Slide11MitKI(_: SlideProps) {
  const sprache = gaussPath(3.5);
  const code = gaussPath(14);
  return (
    <div className="deck-root dark" style={{ background: "var(--svo-dunkelblau)" }}>
      <div className="deck-bg" aria-hidden="true" />

      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1">Arbeiten mit KI</div>
        <h1 className="deck-title rise rise-2">
          Mit KI bauen wir <br />
          <span className="accent">verlässliche Software</span>
        </h1>
        <p className="deck-sub rise rise-3">
          Die KI ist hier das Werkzeug beim Bauen — was am Ende läuft, ist ganz
          normale, deterministische Software.
        </p>
      </header>

      <div className="deck-cols2" style={{ gap: 20 }}>
        <div className="col-card rise rise-4">
          <div className="col-eyebrow">Beim Bauen</div>
          <h4>KI als Entwicklungs-Turbo</h4>
          <p>
            Wir beschreiben, was gebraucht wird — die KI schreibt den Code,
            wir prüfen und testen. So ist das SVO OS entstanden: eine Plattform,
            gebaut im Dialog mit KI.
          </p>
        </div>
        <div className="col-card rise rise-5">
          <div className="col-eyebrow">Im Betrieb</div>
          <h4>Danach: kein Würfeln mehr</h4>
          <p>
            Die fertige Software rechnet jeden Tag exakt gleich — keine
            Halluzination, kein Zufall. Die KI war Werkzeug, nicht Bauteil.
          </p>
        </div>
      </div>

      {/* Rückgriff auf die LLM-Folie: beim Code ist die Kurve spitz — und
          Beleg statt Behauptung (Zählung 20.09.2026, git ls-files). */}
      <div
        className="rise rise-6"
        style={{
          display: "flex",
          gap: "clamp(2rem, 5vw, 4.5rem)",
          alignItems: "center",
          justifyContent: "center",
          margin: "1.5rem auto 0",
        }}
      >
        <div style={{ width: "min(480px, 44vw)", textAlign: "center" }}>
          <svg
            viewBox="0 0 520 175"
            role="img"
            aria-label="Breite Kurve bei Sprache, spitze Kurve bei Code"
            style={{ width: "100%", height: "auto", display: "block" }}
          >
            <path d={sprache} fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="2.5" strokeDasharray="6 6" />
            <path d={`${code} L510,140 L10,140 Z`} fill="rgba(0,141,247,0.22)" stroke="none" />
            <path d={code} fill="none" stroke="var(--svo-blau, #008DF7)" strokeWidth="3" />
            <line x1="10" y1="140" x2="510" y2="140" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
            <text x="260" y="14" textAnchor="middle" fill="#fff" fontSize="13" fontWeight="600">
              Code
            </text>
            <text x="92" y="72" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="12">
              Sprache
            </text>
            <text x="260" y="160" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="11">
              fast nur eine richtige Fortsetzung
            </text>
          </svg>
          <div style={{ marginTop: 6, fontSize: "0.85rem", color: "rgba(255,255,255,0.6)" }}>
            Warum das funktioniert: Beim Code ist die Kurve spitz — und jeden
            Flanken-Fehler fängt der Test in Sekunden.
          </div>
        </div>

        <div style={{ display: "grid", gap: "0.9rem", textAlign: "left" }}>
          {[
            { value: "650.000+", label: "Zeilen Code" },
            { value: "1.100+", label: "Test-Dateien als Sicherheitsnetz" },
            { value: "1", label: "Entwickler" },
          ].map((s) => (
            <div key={s.label} style={{ display: "flex", alignItems: "baseline", gap: "0.75rem" }}>
              <div
                style={{
                  fontSize: "clamp(1.8rem, 3.2vw, 2.8rem)",
                  fontWeight: 300,
                  color: "var(--svo-blau)",
                  lineHeight: 1.05,
                  minWidth: "5.5ch",
                }}
              >
                {s.value}
              </div>
              <div style={{ fontSize: "clamp(0.85rem, 1.2vw, 1rem)", color: "rgba(255,255,255,0.7)" }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      <footer className="deck-foot">
        <span>Genau das probiert ihr gleich selbst: beschreiben — die KI baut.</span>
      </footer>
    </div>
  );
}
